import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Banknote, Plus, Filter, Trash2 } from 'lucide-react';
import { OwnerLayout } from '../../../components/OwnerLayout';
import { PortfolioTabs } from '../../../components/PortfolioTabs';
import api from '../../../api';
import { encodeLan } from '../../../api';

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`} style={{ bottom: '24px' }}>{msg}</div>;
}

function fmtINR(n) {
  const num = Number(n || 0);
  return `\u20B9${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

const DPD_BUCKETS = ['0', '1-30', '31-60', '61-90', '91-120', '120+'];

const EMPTY = {
  loanAccountNumber: '', clientId: '', branchId: '', productType: 'personal_loan',
  disbursementDate: '', repaymentFrequency: 'monthly', firstEmiDate: '', installmentDueDay: 1,
  emiAmount: 0, outstandingPrincipal: 0, outstandingInterest: 0, penaltyAmount: 0,
  totalOverdueAmount: 0, lastPaidDate: '', assignedToUserId: '', supervisingManagerId: '',
};

export default function RecoveryLoans() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialClientId = searchParams.get('clientId') || '';
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('au_user') || '{}'); } catch { return {}; } })();
  const canAssign = currentUser.role === 'owner' || currentUser.role === 'manager';

  const [loans, setLoans] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [bucketFilter, setBucketFilter] = useState('');
  const [clientIdFilter, setClientIdFilter] = useState(initialClientId);
  const [agentFilter, setAgentFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [toast, setToast] = useState({ show: false, msg: '' });

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchFilter) params.branchId = branchFilter;
      if (clientIdFilter) params.clientId = clientIdFilter;
      if (agentFilter) params.assignedToUserId = agentFilter;
      const [loansRes, brRes, usersRes, schemesRes] = await Promise.all([
        api.get('/recovery/loans', { params }),
        api.get('/branches'),
        api.get('/users/access').catch(() => ({ data: [] })),
        api.get('/loan-schemes', { params: { activeOnly: true } }).catch(() => ({ data: [] })),
      ]);
      setLoans(loansRes.data || []);
      setBranches(brRes.data || []);
      setUsers(usersRes.data || []);
      setSchemes(schemesRes.data || []);
    } catch (err) {
      console.error(err);
      showToast('Failed to load loans');
    } finally {
      setLoading(false);
    }
  }, [branchFilter, clientIdFilter, agentFilter]);

  useEffect(() => { load(); }, [load]);

  const filteredLoans = useMemo(() => {
    if (!bucketFilter) return loans;
    return loans.filter((l) => (l.dpdBucket || '0') === bucketFilter);
  }, [loans, bucketFilter]);

  const branchUsers = useMemo(() => {
    if (!form.branchId) return [];
    return users.filter((u) => u.branchId === form.branchId && u.isActive);
  }, [users, form.branchId]);

  const openCreate = () => {
    setForm({ ...EMPTY, branchId: branchFilter || (branches[0]?.id || ''), productType: schemes[0]?.slug || 'personal_loan' });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.loanAccountNumber.trim() || !form.clientId.trim() || !form.branchId || !form.disbursementDate || !form.firstEmiDate) {
      showToast('Loan #, Client ID, branch, disbursement & first EMI dates are required');
      return;
    }
    try {
      const payload = {
        ...form,
        installmentDueDay: parseInt(form.installmentDueDay, 10) || 1,
        emiAmount: parseFloat(form.emiAmount) || 0,
        outstandingPrincipal: parseFloat(form.outstandingPrincipal) || 0,
        outstandingInterest: parseFloat(form.outstandingInterest) || 0,
        penaltyAmount: parseFloat(form.penaltyAmount) || 0,
        totalOverdueAmount: parseFloat(form.totalOverdueAmount) || 0,
        lastPaidDate: form.lastPaidDate || null,
        assignedToUserId: form.assignedToUserId || null,
        supervisingManagerId: form.supervisingManagerId || null,
      };
      await api.post('/recovery/loans', payload);
      setShowModal(false);
      showToast('Loan created');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create loan');
    }
  };

  const deleteLoan = async (loan) => {
    if (!window.confirm(`Delete loan ${loan.loanAccountNumber} for ${loan.customerName || loan.clientId}?\n\nThis will permanently remove the loan, recovery case, all actions, promises and payments.`)) return;
    try {
      await api.delete(`/recovery/loans/${encodeLan(loan.loanAccountNumber)}`);
      showToast('Loan deleted');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Delete failed');
    }
  };

  const bucketBadgeColor = (bucket) => {
    if (bucket === '120+') return '#7f1d1d';
    if (bucket === '91-120') return 'var(--red)';
    if (bucket === '61-90') return '#dc2626';
    if (bucket === '31-60') return '#ea580c';
    if (bucket === '1-30') return '#ca8a04';
    return 'var(--g4)';
  };

  return (
    <OwnerLayout title="Recovery · Loans">
      <PortfolioTabs />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-navy text-lg">Loan Accounts</h2>
          <p className="text-sm text-gray">{filteredLoans.length} loan{filteredLoans.length !== 1 ? 's' : ''}{bucketFilter ? ` in ${bucketFilter} DPD` : ''}</p>
        </div>
        <button className="btn-navy" onClick={openCreate}>
          <Plus size={14} style={{ marginRight: '4px' }} /> New Loan
        </button>
      </div>

      {/* Filters */}
      <div className="panel" style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: 'var(--g3)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Filter size={12} /> Filters:
          </span>
          {branches.length > 1 && (
            <select className="form-input" style={{ width: 'auto', minWidth: '160px' }} value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
              <option value="">All branches</option>
              {branches.map((b) => (<option key={b.id} value={b.id}>{b.branchName}</option>))}
            </select>
          )}
          <select className="form-input" style={{ width: 'auto', minWidth: '140px' }} value={bucketFilter} onChange={(e) => setBucketFilter(e.target.value)}>
            <option value="">All DPD buckets</option>
            {DPD_BUCKETS.map((b) => <option key={b} value={b}>DPD {b}</option>)}
          </select>
          <input
            className="form-input"
            style={{ width: 'auto', minWidth: '140px' }}
            placeholder="Client ID filter"
            value={clientIdFilter}
            onChange={(e) => setClientIdFilter(e.target.value)}
          />
          {(bucketFilter || branchFilter || clientIdFilter || agentFilter) && (
            <button className="btn-outline" onClick={() => { setBranchFilter(''); setBucketFilter(''); setClientIdFilter(''); setAgentFilter(''); }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="panel" style={{ padding: '24px', textAlign: 'center', color: 'var(--g3)' }}>Loading…</div>
      ) : filteredLoans.length === 0 ? (
        <div className="panel" style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>
          No loans match these filters.
        </div>
      ) : (
        <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'var(--g1)', borderBottom: '1px solid var(--g2)' }}>
                <th style={thStyle}>Loan #</th>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Product</th>
                <th style={thStyle}>EMI</th>
                <th style={thStyle}>Overdue</th>
                <th style={thStyle}>Bounced</th>
                <th style={thStyle}>DPD</th>
                <th style={thStyle}>Next Due</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {filteredLoans.map((l) => (
                <tr key={l.id || l.loanAccountNumber}
                    style={{ borderBottom: '1px solid var(--g2)', cursor: 'pointer' }}
                    onClick={() => navigate(`/owner/recovery/cases/${encodeLan(l.loanAccountNumber)}`)}>
                  <td style={tdStyle}><strong>{l.loanAccountNumber}</strong></td>
                  <td style={tdStyle}>
                    <div
                      style={{ fontWeight: 600, color: 'var(--navy)', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/owner/recovery/customers/${encodeURIComponent(l.clientId)}`); }}
                      title="Open customer profile"
                    >{l.customerName || '—'}</div>
                    <div style={{ fontSize: '11px', color: 'var(--g3)' }}>{l.clientId}{l.customerPhone ? ` · ${l.customerPhone}` : ''}</div>
                  </td>
                  <td style={tdStyle}>{l.productType}</td>
                  <td style={tdStyle}>{fmtINR(l.emiAmount)}</td>
                  <td style={tdStyle}><strong style={{ color: 'var(--red)' }}>{fmtINR(l.totalOverdueAmount)}</strong></td>
                  <td style={tdStyle}>
                    {l.bouncedEmis > 0 ? (
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, color: 'white', background: 'var(--red)' }}>
                        {l.bouncedEmis} EMI
                      </span>
                    ) : (
                      <span style={{ fontSize: '11px', color: 'var(--g3)' }} title="No bounced EMI – overdue is current-cycle accrued interest">
                        Current cycle
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, color: 'white', background: bucketBadgeColor(l.dpdBucket) }}>
                      {l.dpdDays ?? 0}d · {l.dpdBucket || '0'}
                    </span>
                  </td>
                  <td style={tdStyle}>{l.nextDueDate || '—'}</td>
                  <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button className="agent-view-link" onClick={() => navigate(`/owner/recovery/cases/${encodeLan(l.loanAccountNumber)}`)}>
                        Open case
                      </button>
                      <button
                        className="agent-view-link text-red"
                        onClick={() => deleteLoan(l)}
                        title="Delete loan and all recovery history"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="owner-modal-overlay open" onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="owner-modal" style={{ width: '560px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-head">
              <div className="modal-icon"><Banknote size={16} /></div>
              <div>
                <div className="modal-title">New Loan Account</div>
                <div className="modal-subtitle">Link to an existing customer by Client ID</div>
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="grid grid-cols-2 gap-3">
                <div className="form-group">
                  <label className="form-label">Loan Account #</label>
                  <input className="form-input" value={form.loanAccountNumber} onChange={(e) => setForm((p) => ({ ...p, loanAccountNumber: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Client ID</label>
                  <input className="form-input" value={form.clientId} onChange={(e) => setForm((p) => ({ ...p, clientId: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Branch</label>
                  <select className="form-input" value={form.branchId} onChange={(e) => setForm((p) => ({ ...p, branchId: e.target.value, assignedToUserId: '', supervisingManagerId: '' }))}>
                    <option value="">Select branch</option>
                    {branches.filter((b) => b.isActive).map((b) => (<option key={b.id} value={b.id}>{b.branchName}</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Loan Scheme</label>
                  <select className="form-input" value={form.productType} onChange={(e) => setForm((p) => ({ ...p, productType: e.target.value }))}>
                    {schemes.length === 0 ? (
                      <option value="">No schemes configured — add in Products & Schemes</option>
                    ) : schemes.map((s) => (
                      <option key={s.id} value={s.slug}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Disbursement Date</label>
                  <input className="form-input" type="date" value={form.disbursementDate} onChange={(e) => setForm((p) => ({ ...p, disbursementDate: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">First EMI Date</label>
                  <input className="form-input" type="date" value={form.firstEmiDate} onChange={(e) => setForm((p) => ({ ...p, firstEmiDate: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Frequency</label>
                  <select className="form-input" value={form.repaymentFrequency} onChange={(e) => setForm((p) => ({ ...p, repaymentFrequency: e.target.value }))}>
                    <option value="monthly">Monthly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="weekly">Weekly</option>
                    <option value="daily">Daily</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Due Day</label>
                  <input className="form-input" type="number" min={1} max={31} value={form.installmentDueDay} onChange={(e) => setForm((p) => ({ ...p, installmentDueDay: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">EMI Amount</label>
                  <input className="form-input" type="number" value={form.emiAmount} onChange={(e) => setForm((p) => ({ ...p, emiAmount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Total Overdue</label>
                  <input className="form-input" type="number" value={form.totalOverdueAmount} onChange={(e) => setForm((p) => ({ ...p, totalOverdueAmount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Outstanding Principal</label>
                  <input className="form-input" type="number" value={form.outstandingPrincipal} onChange={(e) => setForm((p) => ({ ...p, outstandingPrincipal: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Outstanding Interest</label>
                  <input className="form-input" type="number" value={form.outstandingInterest} onChange={(e) => setForm((p) => ({ ...p, outstandingInterest: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Penalty</label>
                  <input className="form-input" type="number" value={form.penaltyAmount} onChange={(e) => setForm((p) => ({ ...p, penaltyAmount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Paid Date</label>
                  <input className="form-input" type="date" value={form.lastPaidDate} onChange={(e) => setForm((p) => ({ ...p, lastPaidDate: e.target.value }))} />
                </div>
                {canAssign && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Assigned Agent</label>
                      <select className="form-input" value={form.assignedToUserId} onChange={(e) => setForm((p) => ({ ...p, assignedToUserId: e.target.value }))}>
                        <option value="">Unassigned</option>
                        {branchUsers.filter((u) => u.role === 'agent').map((u) => (<option key={u.id} value={u.id}>{u.name} ({u.employeeId})</option>))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Supervising Manager</label>
                      <select className="form-input" value={form.supervisingManagerId} onChange={(e) => setForm((p) => ({ ...p, supervisingManagerId: e.target.value }))}>
                        <option value="">None</option>
                        {branchUsers.filter((u) => u.role === 'manager').map((u) => (<option key={u.id} value={u.id}>{u.name} ({u.employeeId})</option>))}
                      </select>
                    </div>
                  </>
                )}
                {!canAssign && (
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: 6 }}>
                      Assignment to a field agent or supervising manager is done by an Owner or Manager after creation.
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <button className="btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn-navy" onClick={save}>Create Loan</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast.msg} show={toast.show} />
    </OwnerLayout>
  );
}

const thStyle = { textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: 'var(--g4)', letterSpacing: '0.04em' };
const tdStyle = { padding: '10px 14px', verticalAlign: 'middle' };
